import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useState } from "react";
import { Badge, Button, Divider, Flex, Modal, Space, Spin, Table } from "antd";
import { LeftOutlined, PlusSquareOutlined } from "@ant-design/icons";
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../../util/helper";
import { DeviceContext, DeviceResponseType, UpdateDeviceContext } from "../../../../context/deviceContext";
import dayjs from "dayjs";
import { CompanyContext, UpdateCompanyContext } from "../../../../context/companyContext";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGE_SIZE = 6;

export function CompanyDeviceList() {
  const { t } = useTranslation();
  const { deviceList, devicePagination } = useContext(DeviceContext);
  const { refreshDeviceList, deleteDevice } = useContext(UpdateDeviceContext);
  const { company } = useContext(CompanyContext);
  const { refreshCompany } = useContext(UpdateCompanyContext);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [isPageReady, setIsPageReady] = useState(false)

  const currentPage = devicePagination.page || 1;
  const currentPageSize = devicePagination.pageSize || DEFAULT_PAGE_SIZE;

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadDeviceList = async () => {
      setIsPageReady(false)

      if (!companyId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      try {
        const companyResult = await refreshCompany(companyId)

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return
        }

        await refreshDeviceList(companyId, { page: 1 })

        if (isCancelled) {
          return
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating company device list.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadDeviceList()

    return () => {
      isCancelled = true
    }
  }, [companyId, navigate, refreshCompany, refreshDeviceList]);

  const onClickManage = useCallback((id: string) => {
    if (!companyId) return;
    const result = PATH_INFO.COMPANY_DEVICE_MANAGE.PATH.replace(':companyId', companyId);
    const _result = result.replace(':deviceId', id);

    navigate(_result);
  }, [companyId, navigate]);

  const onClickDelete = useCallback(async (id: string) => {
    const res = await deleteDevice(id);
    if (res) {
      if (companyId) {
        const pageSize = DEFAULT_PAGE_SIZE;
        const totalAfterDelete = Math.max(devicePagination.total - 1, 0);
        const maxPage = Math.max(1, Math.ceil(totalAfterDelete / pageSize));
        const targetPage = Math.min(devicePagination.page || 1, maxPage);
        await refreshDeviceList(companyId, { page: targetPage });
      }
      Modal.success({ content: t('device.deleteSucceeded'), centered: true });
    } else {
      Modal.error({ content: t('messages.processFailed'), centered: true });
    }
  }, [deleteDevice, refreshDeviceList, companyId, devicePagination.page, devicePagination.total]);

  const handleTableChange = useCallback((pagination: TablePaginationConfig) => {
    if (!companyId) return;
    const nextPage = pagination.current ?? currentPage;

    if (nextPage === currentPage) {
      return;
    }

    refreshDeviceList(companyId, { page: nextPage });
  }, [companyId, currentPage, refreshDeviceList]);

  const columns: ColumnsType<DeviceResponseType> = [
    {
      title: t('device.initialSettings'),
      dataIndex: 'initial_settings',
      key: 'initial_settings',
      render: (_: any, record: DeviceResponseType) => (
        // record === true ? (<CheckCircleOutlined className="text-green-500 text-xl" />) : (<CloseCircleOutlined className="text-red-500 text-xl" />)
        <></> //未使用のため表示しない
      ),
      className: "text-center",
      width: 90,
    },
    {
      title: t('device.location'),
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: t('device.deviceName'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('device.apiKey'),
      dataIndex: 'api_key',
      key: 'api_key',
      width: 200,
    },
    {
      title: t('device.createdDate'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format(`YYYY-MM-DD`),
      width: 150,
      sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      sortDirections: ['descend', 'ascend']
    },
    {
      title: t('device.updatedDate'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => dayjs(text).format(`YYYY-MM-DD`),
      width: 150,
      sorter: (a, b) => dayjs(a.updated_at).valueOf() - dayjs(b.updated_at).valueOf(),
      sortDirections: ['descend', 'ascend']
    },
    {
      title: t('common.operation'),
      dataIndex: 'action',
      key: 'operation',
      render: (_: any, record: DeviceResponseType) => (
        <Space size='middle'>
          <Button variant='outlined' color='primary' onClick={() => onClickManage(record.id)}>{t('common.edit')}</Button>
          <Button variant='outlined' color='danger' onClick={() => {
            Modal.warning({
              title: t('device.deleteTarget', { name: record.name }),
              content: t('device.deleteConfirmStrict'),
              onOk() { onClickDelete(record.id); },
              okCancel: true,
              centered: true,
            });
          }}>
            {t('common.delete')}
          </Button>
        </Space>
      ),
      width: 145,
      fixed: 'right'
    }
  ];

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t('common.loadingWithDots')} />
      </Flex>
    )
  }

  return (
    <div>
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">{t('common.back')}</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">{t('device.companyListPage', { companyName: company?.company_name ?? '' })}</p>
      </div>
      <Divider />
      <Flex className="w-full" justify="space-between" align="end">
        <Flex align="center" className="ml-3 mb-1">
          <Badge status="processing" color="blue" />
          <span className="pl-1">{t('device.totalCount', { count: devicePagination.total })}</span>
        </Flex>
        <Flex gap='small' justify="end" className="mb-3">
          <Button type="primary" icon={<PlusSquareOutlined />} onClick={() => onClickManage('register')}>{t('device.registerNewShort')}</Button>
        </Flex>
      </Flex>
      <Flex vertical justify="center" className="relative">
        <Table<DeviceResponseType>
          dataSource={deviceList}
          scroll={{ x: 'max-content' }}
          columns={columns}
          bordered
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: devicePagination.total,
            position: ['bottomCenter'],
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          rowKey={(record) => record.id}
        />
      </Flex>
    </div>
  );
}
